import React, { useState } from "react"
import { usePortfolioSummary } from "~/api/portfolio_summaries"
import type { ExposureSlice } from "~/api/portfolio_summaries"
import type { GeographicExposureData } from "~/api/quarterly_reports"
import USMap from "../QuarterlyReport/USMap"
import KpiStrip from "./KpiStrip"
import NarrativeCard from "./NarrativeCard"
import ExposurePieChart from "./ExposurePieChart"
import SeriesPnlChart from "./SeriesPnlChart"
import CashFlowChart from "./CashFlowChart"
import TopList from "./TopList"
import SegToggle from "./SegToggle"

interface PortfolioSummaryProps {
  year: number
  quarter: number
}

type SegmentKey = "vintage" | "asset_class" | "age"
type ExposureBasis = "mv" | "cost"

const SEGMENT_OPTIONS: { key: SegmentKey; label: string }[] = [
  { key: "vintage", label: "Vintage" },
  { key: "asset_class", label: "Asset class" },
  { key: "age", label: "Age" },
]

// The reused USMap component (from QuarterlyReport) expects the fuller
// GeographicExposureData shape. Portfolio Summary only has aggregate MV per
// region, so the per-asset-class breakdown fields are filled with zeros —
// USMap never reads them, it only uses geographic_region/qpcsp_weighting/is_total.
function toGeographicExposureData(
  slices: ExposureSlice[],
  basis: ExposureBasis,
): GeographicExposureData[] {
  const valueFor = (slice: ExposureSlice) =>
    basis === "cost" ? (slice.cost ?? 0) : slice.mv
  const total = slices.reduce((sum, s) => sum + valueFor(s), 0)
  if (total <= 0) return []
  return slices.map((s) => ({
    geographic_region: s.label,
    qpcsp_weighting: (valueFor(s) / total) * 100,
    equity: 0,
    venture_capital: 0,
    real_assets: 0,
    credit: 0,
    is_total: false,
  }))
}

const PortfolioSummary = ({ year, quarter }: PortfolioSummaryProps) => {
  const [segment, setSegment] = useState<SegmentKey>("vintage")
  const [geoBasis, setGeoBasis] = useState<ExposureBasis>("mv")
  const { data: rawData, isLoading, error } = usePortfolioSummary(year, quarter)
  const isBuilding = rawData?.building === true
  const data = isBuilding ? undefined : rawData

  return (
    <div className="b-portfolio-summary-container">
      <div className="b-portfolio-summary-header">
        <div className="b-portfolio-summary-breadcrumb">
          <a href="/manage/status/snapshot_summaries" className="b-link">
            Snapshot Summaries
          </a>
          <span className="b-portfolio-summary-breadcrumb-sep">/</span>
          <span>
            Q{quarter} {year}
          </span>
        </div>
        <div className="b-portfolio-summary-title-row">
          <h1 className="b-portfolio-summary-title">
            Q{quarter} {year} Portfolio Summary
          </h1>
          <span className="b-portfolio-summary-proto-badge">Prototype</span>
          {data && (
            <span
              className={`b-portfolio-summary-status b-portfolio-summary-status--${data.status.toLowerCase()}`}
            >
              {data.status}
            </span>
          )}
          <a
            href={`/manage/status/snapshot_summaries/${year}/${quarter}`}
            className="b-portfolio-summary-export-btn"
          >
            View Snapshot Summary
          </a>
        </div>
        {data && (
          <div className="b-portfolio-summary-tabs-row">
            <div className="b-portfolio-summary-tabs">
              <a
                href={`/manage/status/snapshot_summaries/${year}/${quarter}?group=series`}
                className="b-portfolio-summary-tab"
              >
                Fund investments
              </a>
              <a
                href={`/manage/status/snapshot_summaries/${year}/${quarter}?group=portfolio_company`}
                className="b-portfolio-summary-tab"
              >
                Portfolio companies
              </a>
              <span className="b-portfolio-summary-tab active">Summary</span>
            </div>
            <label className="b-portfolio-summary-period-select">
              <span>As of</span>
              <select
                value={`${year}-${quarter}`}
                onChange={(event) => {
                  const [nextYear, nextQuarter] = event.target.value
                    .split("-")
                    .map(Number)
                  window.location.href = `/manage/status/portfolio_summary/${nextYear}/${nextQuarter}`
                }}
              >
                {data.available_periods.map((period) => (
                  <option
                    key={`${period.year}-${period.quarter}`}
                    value={`${period.year}-${period.quarter}`}
                  >
                    {period.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
        )}
      </div>

      {isLoading && (
        <div className="b-portfolio-summary-loading">
          Loading portfolio summary…
        </div>
      )}

      {isBuilding && (
        <div className="b-portfolio-summary-loading">
          Building portfolio summary… this may take a moment.
        </div>
      )}

      {error && (
        <div className="b-portfolio-summary-error">
          Failed to load portfolio summary: {error.message}
        </div>
      )}

      {data && (
        <>
          <KpiStrip kpis={data.kpis} />

          <NarrativeCard
            narrative={data.narrative}
            periodLabel={data.period.label}
          />

          <div className="b-portfolio-summary-row b-portfolio-summary-row--two">
            <div className="b-portfolio-summary-card">
              <div className="b-portfolio-summary-card-header">
                <div>
                  <h2>Exposure by GICS sector</h2>
                  <div className="b-portfolio-summary-card-sub">
                    By market value · {data.period.label}
                  </div>
                </div>
              </div>
              <div className="b-portfolio-summary-card-body">
                <ExposurePieChart data={data.gics_exposure} />
              </div>
            </div>

            <div className="b-portfolio-summary-card">
              <div className="b-portfolio-summary-card-header">
                <div>
                  <h2>Portfolio segmentation</h2>
                  <div className="b-portfolio-summary-card-sub">
                    By market value · {data.period.label}
                  </div>
                </div>
                <SegToggle
                  options={SEGMENT_OPTIONS}
                  active={segment}
                  onChange={(value) => setSegment(value as SegmentKey)}
                />
              </div>
              <div className="b-portfolio-summary-card-body">
                <ExposurePieChart data={data.segment_exposure[segment]} />
              </div>
            </div>
          </div>

          <SeriesPnlChart series={data.series} />

          <div className="b-portfolio-summary-row b-portfolio-summary-row--two">
            <div className="b-portfolio-summary-card">
              <div className="b-portfolio-summary-card-header">
                <div>
                  <h2>Geographic exposure</h2>
                  <div className="b-portfolio-summary-card-sub">
                    By {geoBasis === "mv" ? "market value" : "cost"} ·{" "}
                    {data.period.label}
                  </div>
                </div>
                <SegToggle
                  options={[
                    { key: "mv", label: "MV" },
                    { key: "cost", label: "Cost" },
                  ]}
                  active={geoBasis}
                  onChange={(value) => setGeoBasis(value as ExposureBasis)}
                />
              </div>
              <div className="b-portfolio-summary-card-body">
                {data.geographic_exposure.length === 0 ? (
                  <div className="b-portfolio-summary-chart-empty">
                    No geographic exposure data available for this period.
                  </div>
                ) : (
                  <>
                    <USMap
                      data={toGeographicExposureData(
                        data.geographic_exposure,
                        geoBasis,
                      )}
                    />
                    <div className="b-portfolio-summary-geo-note">
                      Current company reference data breaks the US into Bree
                      regions and groups non-US/unknown exposure as Other.
                    </div>
                  </>
                )}
              </div>
            </div>

            <CashFlowChart series={data.series} periodLabel={data.period.label} />
          </div>

          <TopList
            companies={data.companies}
            series={data.series}
            periodLabel={data.period.label}
          />
        </>
      )}
    </div>
  )
}

export default PortfolioSummary
